import React, { useState } from 'react';
import { companyInfo } from '../../data/mock';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { ArrowRight, Mail, Briefcase } from 'lucide-react';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Mock submission
    setTimeout(() => {
      toast.success('Thank you for your inquiry. We will respond within one business day.');
      setFormData({ name: '', email: '', company: '', message: '' });
      setIsSubmitting(false);
    }, 1000);
  };

  return (
    <section id="contact" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[#0D2438]"></div>
      
      {/* Subtle background image - Communication/connection */}
      <div className="absolute inset-0 opacity-[0.04]">
        <img 
          src="https://images.unsplash.com/photo-1516321318423-f06f85e504b3?q=80&w=2940&auto=format&fit=crop"
          alt=""
          className="w-full h-full object-cover"
        />
      </div>

      {/* Dots pattern */}
      <div className="absolute inset-0 opacity-[0.04]" style={{
        backgroundImage: `radial-gradient(rgba(91, 181, 197, 0.6) 1px, transparent 1px)`,
        backgroundSize: '40px 40px'
      }}></div>

      {/* Decorative glows */}
      <div className="absolute top-1/4 right-0 w-80 h-80 bg-[#1F7A8C]/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-[#3B82F6]/5 rounded-full blur-[100px] pointer-events-none"></div>

      <div className="container mx-auto px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-0.5 bg-[#5BB5C5] rounded"></div>
          <p className="text-[#E8B86D] text-xs font-medium uppercase tracking-widest">Contact</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          {/* Contact Information */}
          <div>
            <h2 className="text-[#5BB5C5] font-semibold text-2xl lg:text-3xl mb-4">
              Get in Touch
            </h2>
            <p className="text-[#B8C8D8] text-lg mb-8 max-w-lg">
              For inquiries regarding <span className="text-[#5BB5C5]">SAP MDG consulting services</span>, assessments, 
              or partnership opportunities, please contact us.
            </p>

            <div className="space-y-6 mb-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-[rgba(91,181,197,0.2)] to-[rgba(91,181,197,0.1)] flex items-center justify-center flex-shrink-0 border border-[rgba(91,181,197,0.2)]">
                  <Mail className="w-5 h-5 text-[#5BB5C5]" />
                </div>
                <div>
                  <h4 className="text-[#E8B86D] font-medium text-sm mb-1">General Inquiries</h4>
                  <a 
                    href={`mailto:${companyInfo.emails.info}`}
                    className="text-[#5BB5C5] hover:text-[#7AC8D6] transition-colors"
                  >
                    {companyInfo.emails.info}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-[rgba(91,181,197,0.2)] to-[rgba(91,181,197,0.1)] flex items-center justify-center flex-shrink-0 border border-[rgba(91,181,197,0.2)]">
                  <Briefcase className="w-5 h-5 text-[#5BB5C5]" />
                </div>
                <div>
                  <h4 className="text-[#E8B86D] font-medium text-sm mb-1">Sales Inquiries</h4>
                  <a 
                    href={`mailto:${companyInfo.emails.sales}`}
                    className="text-[#5BB5C5] hover:text-[#7AC8D6] transition-colors"
                  >
                    {companyInfo.emails.sales}
                  </a>
                </div>
              </div>
            </div>

            {/* Response Time Notice */}
            <div className="bg-gradient-to-r from-[rgba(91,181,197,0.1)] to-[rgba(91,181,197,0.05)] border-l-4 border-[#5BB5C5] p-5 rounded-r-lg">
              <p className="text-[#8899AA] text-sm">
                All inquiries receive a response within one business day. 
                For urgent matters, please indicate in the subject line.
              </p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-gradient-to-b from-[rgba(255,255,255,0.05)] to-[rgba(255,255,255,0.02)] border border-[rgba(91,181,197,0.15)] p-8 rounded-lg">
            <h3 className="text-[#5BB5C5] font-semibold text-lg mb-6">
              Send Us a Message
            </h3>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-[#B8C8D8] text-sm">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="rounded"
                    placeholder="Your name"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#B8C8D8] text-sm">Email</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="rounded"
                    placeholder="your.email@company.com"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company" className="text-[#B8C8D8] text-sm">Company</Label>
                <Input
                  id="company"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="rounded"
                  placeholder="Your company name"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="message" className="text-[#B8C8D8] text-sm">Message</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  className="rounded resize-none"
                  placeholder="Please describe your inquiry or requirements"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
